import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import continuousServiceService from '../../services/continuousServiceService';
import { supplierService } from '../../services/supplierService';
import SupplierSelector from '../../components/PurchaseOrders/SupplierSelector';
import CreateSupplierModal from '../../components/PurchaseOrders/CreateSupplierModal';
import Toast from '../../components/Common/Toast';

const CreateContinuousService = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    supplier_id: '',
    start_date: new Date().toISOString().split('T')[0],
    notes: '',
    include_areas: false,
  });

  const [suppliers, setSuppliers] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [isCreateSupplierModalOpen, setIsCreateSupplierModalOpen] = useState(false);
  const hasLoadedFormData = useRef(false);

  useEffect(() => {
    if (!hasLoadedFormData.current) {
      hasLoadedFormData.current = true;
      loadFormData();
    }
  }, []);

  const loadFormData = async () => {
    try {
      const suppliersRes = await supplierService.getSuppliers({ active_only: true, entity_provider: true });
      if (suppliersRes.success) {
        setSuppliers(suppliersRes.data);
      }
    } catch (error) {
      console.error('Error al cargar datos del formulario:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar los datos del formulario',
        type: 'error'
      });
    }
  };

  const handleSupplierCreated = async (newSupplier) => {
    try {
      const response = await supplierService.getSuppliers({ active_only: true, entity_provider: true });
      if (response.success && response.data) {
        setSuppliers(response.data);
        setFormData(prev => ({ ...prev, supplier_id: newSupplier.id.toString() }));
      }
    } catch (error) {
      console.error('Error al recargar proveedores:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.supplier_id) {
      newErrors.supplier_id = 'El proveedor es requerido';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'La fecha de inicio es requerida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setToast({
        isOpen: true,
        message: 'Por favor, completa todos los campos requeridos',
        type: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        supplier_id: parseInt(formData.supplier_id),
        start_date: formData.start_date,
        notes: formData.notes || null,
        include_areas: !!formData.include_areas,
      };

      const response = await continuousServiceService.createContinuousService(submitData);

      if (response.success) {
        setToast({
          isOpen: true,
          message: 'Servicio continuo creado exitosamente',
          type: 'success'
        });
        setTimeout(() => {
          navigate(`/continuous-services/${response.data.id}`);
        }, 1500);
      } else {
        setErrors(response.errors || {});
        setToast({
          isOpen: true,
          message: response.message || 'Error al crear el servicio continuo',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al crear servicio continuo:', error);
      setToast({
        isOpen: true,
        message: 'Error al crear el servicio continuo',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Nuevo Servicio Continuo</h1>
          <p className="text-gray-600 mt-1">Crea un nuevo servicio continuo para registrar avances diarios</p>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="space-y-6">
            {/* Proveedor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Proveedor <span className="text-red-500">*</span>
              </label>
              <SupplierSelector
                value={formData.supplier_id}
                onChange={(value) => handleInputChange('supplier_id', value)}
                suppliers={suppliers}
                error={errors.supplier_id}
              />
              {errors.supplier_id && (
                <p className="mt-1 text-sm text-red-600">{errors.supplier_id}</p>
              )}
              <button
                type="button"
                onClick={() => setIsCreateSupplierModalOpen(true)}
                className="mt-2 text-sm text-teal-600 hover:text-teal-700"
              >
                + Crear nuevo proveedor
              </button>
            </div>

            {/* Fecha de Inicio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha de Inicio <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                  errors.start_date ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors.start_date && (
                <p className="mt-1 text-sm text-red-600">{errors.start_date}</p>
              )}
            </div>

            {/* Incluir áreas */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="include_areas"
                checked={formData.include_areas || false}
                onChange={(e) => handleInputChange('include_areas', e.target.checked)}
                className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
              />
              <label htmlFor="include_areas" className="ml-2 text-sm text-gray-700">
                Incluir áreas
              </label>
            </div>
            <p className="text-xs text-gray-500 -mt-2">
              Si está activo, en los registros de avance de este servicio podrás asignar un área. Solo este servicio se verá afectado.
            </p>

            {/* Notas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observaciones
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Observaciones adicionales sobre el servicio continuo..."
              />
            </div>
          </div>

          {/* Botones */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/continuous-services')}
              className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md font-medium transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Creando...' : 'Crear Servicio Continuo'}
            </button>
          </div>
        </form>
      </div>

      {/* Modal de crear proveedor */}
      <CreateSupplierModal
        isOpen={isCreateSupplierModalOpen}
        onClose={() => setIsCreateSupplierModalOpen(false)}
        onSuccess={handleSupplierCreated}
        forceProviderOnly={true}
      />

      {/* Toast */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default CreateContinuousService;
